/**
 * useVariantResolver -- frontend port of the backend per-decoration
 * variant resolver in api/src/services/variant_resolver.py.
 *
 * PR-N2 (rescoped). Replaces the legacy ``variantRoutingMap`` data
 * structure (colorSku -> variantLetter) with a per-decoration resolver
 * that consults the new variant kind + color_skus shape introduced by
 * PR-J. The map's role as an "explicit catch-all override" channel
 * collapses into the existing ``manual_ld`` mechanism (per-color L/D
 * override the distributor sets through ColorsPanel / VariantMenu); the
 * map itself is gone -- it cannot express specific_colors and is not
 * the right keying for the new variant model.
 *
 * Priority order (mirrors the backend resolver):
 *
 *   1. ``specific_colors`` whose ``color_skus`` array contains the SKU.
 *   2. ``catch_all_light`` when effective L/D (manual_ld override OR
 *      auto-classification) resolves to ``light``.
 *   3. ``catch_all_dark`` when effective L/D resolves to ``dark``.
 *   4. ``single_catch_all`` when no light/dark split is configured.
 *   5. ``null`` otherwise.
 *
 * Cross-placement semantics: the resolver is keyed on the LOGICAL pair
 * ``(kind, color_skus)`` -- never on the per-row ``variant.id``. Two
 * placements on the same product, each with their own catch_all_light +
 * catch_all_dark variants, both resolve the SAME kind for the SAME color.
 * A specific_colors entry on placement 1 does NOT bleed to placement 2;
 * each decoration carries its own variant set.
 *
 * Public surface (locked):
 *
 *   const { resolve, resolveKind } = useVariantResolver()
 *
 *   resolve(decoration_id, color_sku): Variant | null
 *     Returns the resolved variant ORM row (the same object reference
 *     held by the productEditor store). null when nothing routes.
 *
 *   resolveKind(decoration_id, color_sku): string | null
 *     Convenience: ``resolve(...).kind`` with a null-safe wrapper. Used
 *     by RoutingStrip / ColorFilmstrip badges to label the routed
 *     variant by kind ("Light variant" / "Dark variant" / "All colors"
 *     / "Custom: ...").
 *
 * Reactivity: the helpers read from ``useProductEditorStore()`` at each
 * call. Vue's reactive proxies will track the reads inside any
 * surrounding ``computed`` or template binding, so consumers get
 * fine-grained recomputation when decorations or manual_ld change.
 * The composable does NOT precompute a Map of colorSku -> variant -- a
 * precomputed Map would invalidate on every variants[] reorder; the
 * per-call approach is O(variants) per (decoration, color) which is
 * trivial at the cardinality real products carry (typically 1-3
 * decorations, < 30 colors, < 5 variants per decoration).
 *
 * Parity with backend (api/src/services/variant_resolver.py):
 * - same priority order
 * - same L/D classification (classifyLDWithName mirrors classify_ld +
 *   manual_ld override on the Python side)
 * - same null-when-no-route semantics
 *
 * A frontend-backend parity check would require fixtures shared between
 * vitest and pytest. The structural test cases in
 * useVariantResolver.test.js mirror the cases in
 * api/tests/test_resolve_batch_endpoint.py.
 */
import { useProductEditorStore } from '@/stores/productEditor'
import { classifyLDWithName } from '@/utils/luminance'
import {
  VARIANT_KIND_CATCH_ALL_DARK,
  VARIANT_KIND_CATCH_ALL_LIGHT,
  VARIANT_KIND_SINGLE_CATCH_ALL,
  VARIANT_KIND_SPECIFIC_COLORS,
} from '@/utils/decorationRouting'

/**
 * Compute the effective L/D verdict for a color SKU, folding the
 * per-product ``manual_ld`` override over the auto-classification.
 *
 * Mirrors ``_effective_ld`` in api/src/services/variant_resolver.py.
 * Returns ``'light'`` / ``'dark'`` / ``null``.
 */
function effectiveLd(color, manualLdMap) {
  const sku = color?.color_sku
  if (sku && manualLdMap && Object.prototype.hasOwnProperty.call(manualLdMap, sku)) {
    const raw = manualLdMap[sku]
    if (raw === 'L' || raw === 'light') return 'light'
    if (raw === 'D' || raw === 'dark') return 'dark'
  }
  const { ld } = classifyLDWithName({
    hex: color?.hex ?? null,
    name: color?.name ?? null,
  })
  return ld === 'light' || ld === 'dark' ? ld : null
}

/**
 * Resolve which Variant routes ``color_sku`` for ``decoration_id``.
 *
 * Pure-ish helper that takes the decoration + color + manual_ld map
 * + color_overrides map explicitly so it can be unit-tested without
 * store mocks.
 *
 * Resolution order (post Option-B):
 *
 *   0. ``color_overrides[color_sku]`` -- if 'light' AND catch_all_light
 *      exists on this decoration, return it. Same for 'dark'. Falls
 *      through to the existing tier order when the matching catch-all
 *      doesn't exist (graceful degradation).
 *   1. ``specific_colors`` whose color_skus contains the SKU.
 *   2/3. catch_all_light / catch_all_dark gated on the L/D verdict
 *      (manual_ld map OR auto-classification).
 *   4. ``single_catch_all`` when no light/dark split is configured.
 *   5. null otherwise.
 */
function resolveVariant(decoration, color, manualLdMap, colorOverrides) {
  const variants = decoration?.variants ?? []
  if (!variants.length) return null
  const sku = color?.color_sku
  if (!sku) return null

  // Bucket variants by kind. Preserve first-seen order so when two
  // variants share a kind (which the schema forbids for catch-alls but
  // not for specific_colors), we resolve deterministically.
  const byKind = {}
  for (const v of variants) {
    if (!v) continue
    const k = v.kind
    if (!k) continue
    if (!byKind[k]) byKind[k] = []
    byKind[k].push(v)
  }

  const hasLight = !!byKind[VARIANT_KIND_CATCH_ALL_LIGHT]
  const hasDark = !!byKind[VARIANT_KIND_CATCH_ALL_DARK]

  // 0) color_overrides short-circuit. User intent beats the tier order.
  if (colorOverrides && typeof colorOverrides === 'object') {
    const flag = colorOverrides[sku]
    if (flag === 'light' && hasLight) {
      return byKind[VARIANT_KIND_CATCH_ALL_LIGHT][0]
    }
    if (flag === 'dark' && hasDark) {
      return byKind[VARIANT_KIND_CATCH_ALL_DARK][0]
    }
  }

  // 1) specific_colors wins.
  const specifics = byKind[VARIANT_KIND_SPECIFIC_COLORS] ?? []
  for (const v of specifics) {
    const list = Array.isArray(v.color_skus) ? v.color_skus : []
    if (list.includes(sku)) return v
  }

  // 2 + 3) catch_all_light / catch_all_dark, gated on L/D verdict.
  if (hasLight || hasDark) {
    const ld = effectiveLd(color, manualLdMap)
    if (ld === 'light' && hasLight) {
      return byKind[VARIANT_KIND_CATCH_ALL_LIGHT][0]
    }
    if (ld === 'dark' && hasDark) {
      return byKind[VARIANT_KIND_CATCH_ALL_DARK][0]
    }
  }

  // 4) single_catch_all when no light/dark split is configured.
  if (byKind[VARIANT_KIND_SINGLE_CATCH_ALL]) {
    return byKind[VARIANT_KIND_SINGLE_CATCH_ALL][0]
  }

  return null
}

export function useVariantResolver() {
  const editor = useProductEditorStore()

  /**
   * Build a {color_sku, name, hex} row for the resolver. Tolerates the
   * supplierMeta cache miss the rest of the editor handles by falling
   * back to a synthesized row (name = SKU, no hex). The L/D classifier
   * uses the name heuristic when hex is absent.
   */
  function lookupColor(colorSku) {
    if (!colorSku) return null
    const colors = editor.supplierMeta?.colors ?? []
    const row = colors.find((c) => c.color_sku === colorSku)
    if (row) return row
    return { color_sku: colorSku, name: colorSku, hex: null }
  }

  function lookupDecoration(decorationId) {
    if (decorationId == null) return null
    const decos = editor.decorations ?? []
    return decos.find((d) => (d.id ?? d._tempId) === decorationId) ?? null
  }

  function getManualLd() {
    const map = editor.getEffectiveAttribute?.('manual_ld')
    return (map && typeof map === 'object') ? map : null
  }

  function getColorOverrides() {
    const map = editor.getEffectiveAttribute?.('color_overrides')
    return (map && typeof map === 'object') ? map : null
  }

  function resolve(decorationId, colorSku) {
    const decoration = lookupDecoration(decorationId)
    if (!decoration) return null
    const color = lookupColor(colorSku)
    if (!color) return null
    return resolveVariant(
      decoration, color, getManualLd(), getColorOverrides()
    )
  }

  function resolveKind(decorationId, colorSku) {
    const v = resolve(decorationId, colorSku)
    return v?.kind ?? null
  }

  return { resolve, resolveKind }
}

// Exported for direct unit testing without a Pinia store. Kept on the
// module so the consumer-side composable can stay thin.
export { resolveVariant, effectiveLd }
